import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  Image,
  StyleSheet,
  PDFViewer,
  Font,
} from "@react-pdf/renderer";
import { createTw } from "react-pdf-tailwind";
import { Request } from "../../hooks/Request";
import { requestAuth } from "../../components/services/RequestService";

Font.registerHyphenationCallback((word) => [word]);

const Unidades = (num) => {
  switch (num) {
    case 1:
      return "un";
    case 2:
      return "dos";
    case 3:
      return "tres";
    case 4:
      return "cuatro";
    case 5:
      return "cinco";
    case 6:
      return "seis";
    case 7:
      return "siete";
    case 8:
      return "ocho";
    case 9:
      return "nueve";
    default:
      return "";
  }
};
const Decenas = (num) => {
  const decena = Math.floor(num / 10);
  const unidad = num - decena * 10;
  switch (decena) {
    case 1:
      switch (unidad) {
        case 0:
          return "diez";
        case 1:
          return "once";
        case 2:
          return "doce";
        case 3:
          return "trece";
        case 4:
          return "catorce";
        case 5:
          return "quince";
        default:
          return "dieci" + Unidades(unidad);
      }
    case 2:
      switch (unidad) {
        case 0:
          return "veinte";
        default:
          return "veinti" + Unidades(unidad);
      }
    case 3:
      return DecenasY("treinta", unidad);
    case 4:
      return DecenasY("cuarenta", unidad);
    case 5:
      return DecenasY("cincuenta", unidad);
    case 6:
      return DecenasY("sesenta", unidad);
    case 7:
      return DecenasY("setenta", unidad);
    case 8:
      return DecenasY("ochenta", unidad);
    case 9:
      return DecenasY("noventa", unidad);
    case 0:
      return Unidades(unidad);
    default:
      return "";
  }
};
const DecenasY = (strSin, numUnidades) => {
  return numUnidades > 0 ? strSin + " y " + Unidades(numUnidades) : strSin;
};
const Centenas = (num) => {
  const centenas = Math.floor(num / 100);
  const decenas = num - centenas * 100;
  switch (centenas) {
    case 1:
      return decenas > 0 ? "ciento " + Decenas(decenas) : "cien";
    case 2:
      return "doscientos " + Decenas(decenas);
    case 3:
      return "trescientos " + Decenas(decenas);
    case 4:
      return "cuatrocientos " + Decenas(decenas);
    case 5:
      return "quinientos " + Decenas(decenas);
    case 6:
      return "seiscientos " + Decenas(decenas);
    case 7:
      return "setecientos " + Decenas(decenas);
    case 8:
      return "ochocientos " + Decenas(decenas);
    case 9:
      return "novecientos " + Decenas(decenas);
    default:
      return Decenas(decenas);
  }
};
const Seccion = (num, divisor, strSingular, strPlural) => {
  const cientos = Math.floor(num / divisor);
  const resto = num - cientos * divisor;
  let letras = "";
  if (cientos > 0) {
    letras = cientos > 1 ? Centenas(cientos) + " " + strPlural : strSingular;
  } else {
    letras = strSingular;
  }
  return letras;
};
const Miles = (num) => {
  const divisor = 1000;
  const cientos = Math.floor(num / divisor);
  const resto = num - cientos * divisor;
  const strMiles = Seccion(num, divisor, "un mil", "mil");
  const strCentenas = Centenas(resto);
  return strMiles === "" || cientos === 0
    ? strCentenas
    : strMiles + (strCentenas ? " " + strCentenas : "");
};
const Millones = (num) => {
  const divisor = 1000000;
  const cientos = Math.floor(num / divisor);
  const resto = num - cientos * divisor;
  const strMillones = Seccion(num, divisor, "un millón", "millones");
  const strMiles = Miles(resto);
  return strMillones === "" || cientos === 0
    ? strMiles
    : strMillones + (strMiles ? " " + strMiles : "");
};
const NumeroALetras = (num) => {
  if (!num || isNaN(num)) return "cero 00/100";
  const data = {
    numero: num,
    enteros: Math.floor(num),
    centavos: Math.round(num * 100) - Math.floor(num) * 100,
  };
  if (data.enteros === 0) return "cero 00/100";
  return (
    Millones(data.enteros) +
    ` ${data.centavos === 0 ? "00" : data.centavos}/100`
  );
};

const formatearFecha = (fechaStr) => {
  if (!fechaStr) return "";
  const meses = [
    "enero",
    "febrero",
    "marzo",
    "abril",
    "mayo",
    "junio",
    "julio",
    "agosto",
    "septiembre",
    "octubre",
    "noviembre",
    "diciembre",
  ];
  const partes = fechaStr.split("-");
  if (partes.length !== 3) return fechaStr;
  return `${partes[2].substring(0, 2)} de ${meses[parseInt(partes[1]) - 1]} de ${partes[0]}`;
};

const tw = createTw({ theme: { fontFamily: { sans: "Helvetica" } } });

const styles = StyleSheet.create({
  page: {
    paddingTop: 0,
    padding: 40,
    fontFamily: "Helvetica",
    fontSize: 10.5,
    lineHeight: 1.2,
    paddingBottom: 40,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  logo: { width: 140 },
  title: { fontSize: 10.5, textAlign: "center", marginBottom: 15 },
  p: { textAlign: "justify", marginBottom: 10 },
  bold: { fontWeight: "bold" },
  signatures: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 50,
    paddingHorizontal: 20,
  },
  signBox: {
    width: "44%",
    alignItems: "flex-start",
  },
  signatureImageBox: {
    height: 150,
    width: "100%",
    alignItems: "center",
    justifyContent: "flex-end",
    marginBottom: -38,
  },
  signatureImage: {
    width: 460,
    maxHeight: 300,
    objectFit: "contain",
  },
  signatureLine: {
    width: "100%",
    borderTop: "1pt solid black",
    paddingTop: 5,
  },
  pageNumber: {
    position: "absolute",
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: "center",
    fontSize: 8,
    color: "gray",
  },
});

export const TransportContractPdf = (props) => {
  const { procedureId, companyId, parentProcedure } = props;

  const listFields = [
    "subastaDeTransporte",
    "antecedentesContratoDeTransporte",
    "tipoServicio",
    "objetoContratoDeTransporte",
    "diasPlazo",
    "diasPlazoAduana",
    "precioMonto",
    "responsableDeAlmacen",
    "cuidadDeLaNotaria",
    "fechaContrato",
    "company",
    "tipoDeImportacion",
    "cantidad",
    "modalidadCargaTransporte",
    "modalidadDeCarga",
    "descripciónGeneralDeLaCompra",
    "puertoDeDescarga",
    "puertoOCiudadDeEmbarque",
    "proveedor",
    "paísDeDescarga",
  ];

  const ListItem = ({ children }) => (
    <View style={[styles.p, { flexDirection: "row", paddingLeft: 10 }]}>
      <Text style={{ marginRight: 5 }}>• </Text>
      <Text style={{ flex: 1 }}>{children}</Text>
    </View>
  );

  const queryParams = listFields
    .map((field) => `fieldnames=${field}`)
    .join("&");

  const { data: dataValues, loading: loadingValues } = Request({
    urlApi: `/Procedure/${procedureId}/dataValues?${queryParams}`,
  });

  const { data: procedureDetail } = Request({
    urlApi: `/Procedure/${procedureId}/Detail`,
  });

  const [parentTipoImportacion, setParentTipoImportacion] = React.useState("");

  React.useEffect(() => {
    const parentId =
      props.parentProcedureId ||
      procedureDetail?.fatherProcedureId ||
      procedureDetail?.fatherProcedure?.id ||
      procedureDetail?.fatherProcedure?.procedureId;

    if (parentId) {
      requestAuth(
        "get",
        `/Procedure/${parentId}/dataValues?fieldnames=tipoDeImportacion`,
      )
        .then((res) =>
          setParentTipoImportacion(res.data?.tipoDeImportacion || ""),
        )
        .catch(() => setParentTipoImportacion(""));
    }
  }, [procedureDetail]);

  const tipoImportacion =
    dataValues?.tipoDeImportacion || parentTipoImportacion || "";

  const numRef = procedureDetail?.numRef || "";

  const selectedTransportItem = dataValues?.subastaDeTransporteSubDatas?.find(
    (s) => String(s.description3).toLowerCase() === "true",
  );

  const providerName = selectedTransportItem ? selectedTransportItem.label : "";

  const { data: providersList, loading: loadingProviders } = Request({
    urlApi: `/Dropdown/providers/options`,
  });

  const { data: companyInfo } = Request({
    urlApi: companyId ? `/company/${companyId}` : null,
  });

  const { data: companyDetails } = Request({
    urlApi: companyId ? `/company/${companyId}/details` : null,
  });

  const { data: routeData, loading: LoadingDataRoute } = Request({});

  const providerDetail = providersList?.find((p) => p.name === providerName);

  const providerData = providerDetail?.data
    ? JSON.parse(providerDetail.data)
    : {};

  if (!loadingValues || !loadingProviders || !dataValues) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <p>Generando contrato...</p>
      </div>
    );
  }

  const {
    antecedentesContratoDeTransporte = "...",
    tipoServicio = "exclusivo internacional",
    objetoContratoDeTransporte = "...",
    diasPlazo = "7",
    diasPlazoAduana = "5",
    precioMonto = 0,
    responsableDeAlmacen = "...",
    cuidadDeLaNotaria = "Cochabamba",
    fechaContrato = "2026-01-01",
  } = dataValues;

  const company = dataValues.company || companyInfo || {};
  const companyDetailsMap = {};
  if (Array.isArray(companyDetails)) {
    companyDetails.forEach((d) => {
      if (d.value && d.value !== "[]" && d.value !== "{}") {
        companyDetailsMap[d.label] = d.value;
      }
    });
  }

  const cantidadData = dataValues.cantidadSubDatas?.[0]?.description2
    ? JSON.parse(dataValues.cantidadSubDatas[0].description2)
    : {};

  const capitalize = (text) =>
    text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : "";

  const quantity = cantidadData.quantity || 0;
  const containerType = cantidadData.containerType || "";
  const modalidadDeCarga = dataValues.modalidadDeCarga;
  const descripcionGeneralDeLaCompra = dataValues.descripciónGeneralDeLaCompra;
  const puertoDescarga = capitalize(dataValues.puertoDeDescarga);
  const paisDescarga = capitalize(dataValues.paísDeDescarga);
  const companyName = company.razonSocial || "PLASTIFORTE S.R.L.";
  const companySigla = company.sigla || "PLASTIFORTE";
  const isPlastiforte = [companyName, companySigla].some((value) =>
    String(value || "")
      .toUpperCase()
      .includes("PLASTIFORTE"),
  );
  const legalRepresentativeSignatureSrc = isPlastiforte
    ? "/images/firma-representante-plastiforte.png"
    : "";
  const companyTestimonio =
    company.testimonio || companyDetailsMap["testimonio"] || "S/N";
  const companyFundempresa =
    company.fundempresa || companyDetailsMap["fundempresa"] || "S/N";
  const totalPrice = Number(precioMonto || 0);

  const isWallhaven = companyName.includes("WALLHAVEN");
  const logoSrc =
    company.logoUrl ||
    (isWallhaven
      ? "/images/logo-Wallhaven.png"
      : "/images/logo-Plastiforte-v2.png");

  const MyDoc = (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <View style={styles.header} fixed>
          <Image src={logoSrc} style={styles.logo} />
          <View style={tw("text-right text-[8px]")}>
            <Text style={styles.bold}>
              N° {numRef || dataValues.procNumber || "438"}
            </Text>
            <Text>PR-COM-121-R201 v2</Text>
          </View>
        </View>

        <Text style={styles.title}>
          CONTRATO DE PRESTACION DE SERVICIOS DE TRANSPORTE EXCLUSIVO
          INTERNACIONAL {"\n"}DE CARGA DE IMPORTACIONES
        </Text>

        <Text style={styles.p}>
          Conste por el presente documento privado, que con el solo
          reconocimiento de firmas y rubricas ante la autoridad competente
          surtirá los efectos de un documento público, un Contrato de prestación
          de Servicio de Transporte exclusivo internacional de carga, sujeto a
          los términos, clausulas y condiciones que a continuación se detallan:
        </Text>

        <Text style={styles.p}>
          <Text style={styles.bold}>PRIMERA.- ( PARTES ).-{"\n"}</Text>
          <Text style={styles.bold}>1. {companyName}</Text>, legalmente
          constituida, con Matrícula de Comercio {companyFundempresa}, NIT No.{" "}
          {company.nit || "S/N"}, con domicilio {company.direccion || "S/N"},
          representada legalmente por la Sra.{" "}
          {company.legalRepresentativeName || "S/N"}, mayor de edad, hábil por
          derecho, con C.I. No. {company.legalRepresentativeCI || "S/N"}, con
          domicilio en {company.domicilioLegal || "S/N"}, de la ciudad de{" "}
          {company.legalRepresentativeCIEmision || "S/N"}, , en calidad de
          apoderada, debidamente acreditada mediante escritura pública de
          testimonio de poder {companyTestimonio}, de fecha 2 de febrero de
          2025, ortorgado ante Notario de Fe Publica N-75 Dr. Jhonny Mena Soliz
          del distrito judicial de Cochabamba, que para los efectos del presente
          contrato se denominara <Text style={styles.bold}>{companySigla}</Text>
          .{"\n\n"}
          <Text style={styles.bold}>
            2. LA EMPRESA {providerName || " "}
          </Text>{" "}
          legalmente constituida, con Matrícula de Comercio No.{" "}
          {providerData.matricula_comercio || " "}, NIT No.{" "}
          {providerData.nit_ruc || providerData.nit_tax_id || " "}, con
          domicilio {providerData.direccion || " "}, representada por{" "}
          {providerData.representante_legal || " "}, mayor de edad, habil por
          derecho, C.I. No. {providerData.ci_dni || " "}, expedido en{" "}
          {providerData.ciudad || "CBBA"}, con domicilio en{" "}
          {providerData.dir_rep_legal || " "}, en su calidad de Representante
          Legal tal cual se evidencia del testimonio No.{" "}
          {providerData.n_testimonio || ""} de{" "}
          {providerData.fecha_testimonio || ""} otorgado ante{" "}
          {providerData.otorgado_ante || ""}, Notaría de fe pública No.{" "}
          {providerData.n_notaria || " "} que, para efectos del presente
          contrato, se denominara LA EMPRESA.
        </Text>

        <Text style={styles.p}>
          <Text style={styles.bold}>
            SEGUNDA.- (ANTECEDENTES).- {companySigla}
          </Text>
          , requiere la prestación de servicios de transporte exclusivo
          internacional de carga, para el traslado de{" "}
          {tipoImportacion || antecedentesContratoDeTransporte}.{"\n\n"}
          El precio acordado entre partes se sujeta a la cotización de trasporte
          presentada por <Text style={styles.bold}>LA EMPRESA</Text> y aprobada
          por <Text style={styles.bold}>{companySigla}</Text>, la cual no puede
          sufrir modificación o incremento alguno y forma parte integrante del
          presente contrato.{"\n\n"}
          <Text style={styles.bold}>LA EMPRESA</Text>, dedicada al rubro de
          Transporte de carga Internacional terrestre, declara que se encuentra
          capacitada para satisfacer las necesidades objeto del presente
          contrato a favor de <Text style={styles.bold}>{companySigla}</Text> y
          cumplir a cabalidad la prestación de servicios requeridos, en merito a
          contar con la experiencia debida en el rubro, de manera eficiente e
          idónea, conociendo y aceptando todas las particularidades del servicio
          mencionado.
        </Text>

        <Text style={styles.p}>
          <Text style={styles.bold}>TERCERA.- (OBJETO). -</Text> Por medio del
          presente contrato, <Text style={styles.bold}>LA EMPRESA</Text> se
          obliga, a favor y satisfacción de{" "}
          <Text style={styles.bold}>{companySigla}</Text>, a prestar el
          "Servicio de Transporte exclusivo internacional de carga" en apego al
          presente contrato, para la importación identificada con el código{" "}
          {numRef || dataValues.procNumber || "PCIME-438 MGI"}{" "}
          {parentProcedure?.proveedor || ""}. Este servicio contempla el
          traslado de {quantity} {quantity > 1 ? "contenedores" : "contenedor "}
          de {containerType}, como carga {modalidadDeCarga} -{" "}
          {descripcionGeneralDeLaCompra ||
            parentProcedure?.descripciónGeneralDeLaCompra}
          , desde {puertoDescarga}-{paisDescarga}, hasta la planta de producción
          Santivañez, ubicado a 30km. de la ciudad de Cochabamba. El servicio se
          regirá por los términos y condiciones del presente contrato y la
          cotización adjunta.
        </Text>

        <Text style={styles.p}>
          <Text style={styles.bold}>CUARTA.- (NATURALEZA DEL CONTRATO).-</Text>{" "}
          El presente contrato es suscrito de acuerdo a lo señalado por el Art
          732 y siguientes, del Código Civil vigente, por lo que es de
          naturaleza estrictamente civil, sin que exista ninguna relación
          laboral entre los contratantes. {"\n\n"}{" "}
        </Text>
        <Text style={styles.p}>
          Siendo <Text style={styles.bold}>LA EMPRESA</Text>, directa
          responsable por su personal al ser un servicio tercializado por parte
          de <Text style={styles.bold}>{companySigla}</Text>, por lo que{" "}
          <Text style={styles.bold}>LA EMPRESA</Text> debe asumir toda
          obligación laboral y los beneficios sociales de cualquier índole en
          relación a su personal o contratados, se deja expresamente
          establecido, que el personal que contrate y ocupe{" "}
          <Text style={styles.bold}>LA EMPRESA</Text> a través suyo o mediante
          un subcontrato, para la realización del Servicio, no tendrá ninguna
          relación de dependencia obrero patronal con{" "}
          <Text style={styles.bold}>{companySigla}</Text>.
        </Text>

        <Text style={styles.p}>
          <Text style={styles.bold}>QUINTA.- (PLAZO).-</Text> El plazo para
          realizar el transporte terrestre objeto de este contrato será de{" "}
          {diasPlazo} días calendario, a partir de la fecha en que la carga haya
          sido debidamente cargada en el vehículo de transporte.{" "}
          <Text style={styles.bold}>LA EMPRESA</Text> otorgara {diasPlazoAduana}{" "}
          días calendarios libres en Aduana correspondiente dentro el plazo
          máximo mencionado líneas arriba, con el fin de realizar los trámites
          de desaduanización.{"\n\n"}
          En caso de que <Text style={styles.bold}>LA EMPRESA</Text>, no cumpla
          con los plazos establecidos o exista algún impedimento para ello,{" "}
          <Text style={styles.bold}>LA EMPRESA</Text> tomara las medidas
          necesarias para asegurar el cumplimiento de dichos plazos, aunque esto
          implique costos adicionales con terceros, los cuales serán asumidos en
          su totalidad por la misma.
        </Text>

        <Text style={styles.p}>
          <Text style={styles.bold}>
            SEXTA.- (CONTRAPRESTACION Y FORMA DE PAGO).-
          </Text>{" "}
          El precio final por la ejecución total y completa de los servicios de
          transporte por parte de <Text style={styles.bold}>LA EMPRESA</Text> de
          acuerdo a proforma aceptada, es de{" "}
          <Text style={styles.bold}>
            $us {totalPrice} ({NumeroALetras(totalPrice).toUpperCase()} Dólares
          </Text>{" "}
          de los Estados Unidos de America).{" "}
          <Text style={styles.bold}>{companySigla}</Text>, se compromete a
          cancelar a <Text style={styles.bold}>LA EMPRESA</Text> la suma total
          pactada entre partes señalada líneas arriba una vez que{" "}
          <Text style={styles.bold}>LA EMPRESA</Text> haya entregado a{" "}
          <Text style={styles.bold}>{companySigla}</Text>, a su entera
          conformidad, toda la documentación detallada en el presente contrato.
          {"\n\n"}
          Las partes acuerdan que los montos mencionados serán transferidos a
          nombre de LA EMPRESA una vez sea descargado en almacén acordado.{"\n"}
          Asimismo, LA EMPRESA declara que la contraprestación acordada incluye
          todos los costos directos e indirectos relacionados con la completa
          prestación de los servicios objeto del presente contrato, y que no
          estará sujeta a ningún incremento. {"\n\n"}
          Las partes acuerdan que los gastos de puerto, liberación y gastos en
          destino serán cubiertos por{" "}
          <Text style={styles.bold}>LA EMPRESA</Text>, y{" "}
          <Text style={styles.bold}>{companySigla}</Text> procederá a la
          devolución total de dichos gastos una vez entregados los
          correspondientes respaldos (los cuales deben ser consignados a nombre
          de{" "}
          <Text style={styles.bold}>
            {companyName} NIT {company.nit || "1009109025"}
          </Text>
          ).
        </Text>

        <Text style={styles.p}>
          <Text style={styles.bold}>SEPTIMA.- (DOCUMENTOS INTEGRANTES).-</Text>{" "}
          Forman parte integrante e indivisible del presente Contrato, los
          siguientes documentos:{"\n"}
          1.- Cotización de transporte.{"\n"}
          2.- Copia Lista de Empaque.{"\n"}
          3.- Copia Factura Comercial.{"\n"}
          4.- B/L Original.{"\n"}
        </Text>
        <Text style={styles.p}>
          Las partes acuerdan que estos documentos y todos aquellos que forman
          parte y son requeridos para el proceso de transporte objeto del
          presente contrato, tienen calidad de CONFIDENCIALIDAD, por lo que solo
          pueden ser usados o dispuestos para fines estrictamente de gestión o
          requerimiento legal del proceso de TRANSPORTE.{"\n"}
          Las partes acuerdan que, una vez entregada la carga en destino
          acordado, <Text style={styles.bold}>LA EMPRESA</Text> deberá entregar
          a <Text style={styles.bold}>{companySigla}</Text>, a conformidad, toda
          la documentación correspondiente y detallada a continuación, en un
          plazo máximo de un día hábil a partir de la fecha de entrega, con el
          fin de proceder con la regularización correspondiente ante Aduana.
          {"\n"}
          Los documentos requeridos son las siguientes:
        </Text>
        <Text style={styles.p}>
          1.- MIC/DTA- Manifiesto Internacional de Carga{"\n"}
          2.- CRT - Carta de Porte Internacional por Carretera{"\n"}
          3.- Factura de Transporte Terrestre Internacional{"\n"}
          4.- Nota de Cobranza{"\n"}
          5.- Comprobantes de pagos realizados para la liberación{"\n\n"}
          Todos las comprobantes y documentos deben estar emitidos a nombre de{" "}
          {companyName}.
        </Text>

        <Text style={styles.p}>
          <Text style={styles.bold}>
            OCTAVA.- (REGIMEN POR INCUMPLIMIENTO).-
          </Text>{" "}
          En caso de que <Text style={styles.bold}>LA EMPRESA</Text> no cumpla
          con la programación fijada o las plazas establecidos para la
          prestación de los servicios y/o documentación, objeto del presente
          contrato, por razones atribuibles a su servicio, que no sean caso
          fortuito o fuerza mayor debidamente comprobable,{" "}
          <Text style={styles.bold}>LA EMPRESA</Text> reconocerá a{" "}
          <Text style={styles.bold}>{companySigla}</Text> el 5% del valor del
          contrato por cada día de retraso, además del resarcimiento de daños y
          perjuicios ocasionados por el incumplimiento. Las partes acuerdan que{" "}
          <Text style={styles.bold}>{companySigla}</Text> podrá retener de las
          pagos que adeude a LA EMPRESA por el 5% del total del contrato y/o el
          resarcimiento de daños, si las hubiere.
        </Text>

        <Text style={styles.p}>
          <Text style={styles.bold}>
            NOVENA.- (REPRESENTANTE DE {companySigla}).- {companySigla}
          </Text>
          , designa como responsable para el control, verificación y recepción
          de la carga al Sr. {responsableDeAlmacen}, siendo el indicado para la
          recepción de la carga y documentación.
        </Text>

        <Text style={styles.p}>
          <Text style={styles.bold}>
            DECIMA.- (SUBCONTRATACIÓN Y RESERVA DE DERECHOS).- LA EMPRESA
          </Text>{" "}
          se obliga a notificar a {companySigla} sobre cualquier subcontratación
          de los servicios previstos en el presente Contrato, asumiendo plena
          responsabilidad por las consecuencias derivadas de dicha
          subcontratación, incluyendo los riesgos asociados al cumplimiento de
          sus propias obligaciones. Asimismo, deberá proporcionar a{" "}
          <Text style={styles.bold}>{companySigla}</Text> una copia del contrato
          suscrito con el subcontratista, para efectos de conocimiento.
        </Text>

        {/* CLÁUSULA DECIMA PRIMERA */}
        <Text style={styles.p}>
          <Text style={styles.bold}>
            DECIMA PRIMERA. - (OBLIGACIONES DE LA EMPRESA).-
          </Text>{" "}
          Para el cumplimiento del objeto del presente Contrato,{" "}
          <Text style={styles.bold}>LA EMPRESA</Text> se obliga a:{"\n\n"}
          <ListItem>
            Asumir plena responsabilidad por la prestación de los servicios
            conforme al alcance previsto en este Contrato y sus Anexos.{"\n"}
          </ListItem>
          <ListItem>
            Solicitar por escrito y con la debida anticipación cualquier
            información o documentación requerida a{" "}
            <Text style={styles.bold}>{companySigla}</Text>, evitando demoras o
            contratiempos.{"\n"}
          </ListItem>
          <ListItem>
            Garantizar que sus dependientes respeten la confidencialidad de la
            documentación relacionada con el proceso de transporte.{"\n"}
          </ListItem>
          <ListItem>
            Entregar puntualmente toda la documentación necesaria para los
            trámites de desaduanización y entrega final.{"\n"}
          </ListItem>
          <ListItem>
            Cumplir estrictamente con los plazos establecidos en el presente
            Contrato.{"\n"}
          </ListItem>
          <ListItem>
            Entregar los documentos originales y copias pertinentes al
            responsable o analista de Importaciones de{" "}
            <Text style={styles.bold}>{companySigla}</Text>.{"\n"}
          </ListItem>
          <ListItem>
            Responder por los daños ocasionados en la prestación del servicio.
            {"\n"}
          </ListItem>
          <ListItem>
            Cumplir con todas las disposiciones del presente Contrato.{"\n"}
          </ListItem>
          <ListItem>
            Cumplir con la ruta acordada, estableciendo tiempos de recorrido,
            puntos de control, áreas de descanso y servicios clave.{"\n"}
          </ListItem>
          <ListItem>
            Transportar la carga de manera exclusiva, incluso si existen
            espacios libres.{"\n"}
          </ListItem>
          <ListItem>
            Devolver los contenedores dentro del plazo acordado.{"\n"}
          </ListItem>
          <ListItem>
            Asegurar que los conductores cuenten con agendas telefónicas
            actualizadas, incluyendo contactos de módulos policiales en sus
            rutas, transportistas cercanos, grúas, talleres, ambulancias y
            centros de salud.
          </ListItem>
        </Text>

        {/* CLÁUSULA DECIMA SEGUNDA */}
        <Text style={styles.p}>
          <Text style={styles.bold}>
            DECIMA SEGUNDA. - (SEGURIDAD EN EL TRANSPORTE DE LA MERCANCÍA).-
          </Text>{" "}
          La empresa de transporte se compromete a implementar y mantener
          medidas de seguridad estrictas para garantizar la integridad de la
          carga durante todo el proceso de traslado, cumpliendo con los
          siguientes requisitos:{"\n\n"}
          Las unidades de transporte deberán contar con un Sistema de
          Posicionamiento Global (GPS), o un medio de localización equivalente,
          asegurando el resguardo de los datos de control por un mínimo de un
          (1) año.{"\n"}
          El transportista autoriza a{" "}
          <Text style={styles.bold}>{companySigla}</Text> a realizar auditorías
          de segunda parte cuando lo considere necesario, a fin de verificar el
          cumplimiento de las condiciones pactadas en el presente contrato.
          {"\n"}
          Se deberán implementar mecanismos que restrinjan el acceso al medio y
          unidad de transporte, permitiendo el ingreso únicamente al conductor
          y, opcionalmente, a un relevo, quien deberá cumplir con las mismas
          condiciones de seguridad exigidas al conductor.{"\n\n"}
          <Text style={styles.bold}>LA EMPRESA</Text>, deberá contar con la
          documentación propietaria u otros, de las unidades y permisos de
          viabilidad dentro y fuera del territorio nacional, debidamente
          saneados y al día.{"\n\n"}
          <Text style={styles.bold}>LA EMPRESA</Text>, debe contar con las
          pólizas de seguro pertinentes tanto de la unidad de transporte como de
          los choferes o acompañantes, siendo responsabilidad directa de{" "}
          <Text style={styles.bold}>LA EMPRESA</Text> el personal que presta el
          servicio de transporte u otros afines requeridos para el cumplimiento
          del objeto del presente contrato.{"\n\n"}
          <Text style={styles.bold}>LA EMPRESA</Text>, no puede realizar bajo
          ningún motivo el traslado o cambio de la carga a otras unidades de
          transporte, siendo una prohibición esta condición dentro del presente
          contrato.
        </Text>

        {/* CLÁUSULA DECIMA TERCERA */}
        <Text style={styles.p}>
          <Text style={styles.bold}>
            DECIMA TERCERA.- (SEGURIDAD DE LOS MEDIOS Y UNIDADES DE
            TRANSPORTE).-
          </Text>{" "}
          Las unidades de transporte en espera antes de la descarga deberán
          permanecer en áreas seguras y monitoreadas. Asimismo, deberán estar en
          óptimas condiciones y contar con la revisión técnica, mantenimiento
          correspondiente, SOAT e inspección técnica vehicular, según
          corresponda.
        </Text>

        {/* CLÁUSULA DECIMA CUARTA */}
        <Text style={styles.p}>
          <Text style={styles.bold}>
            DECIMA CUARTA.- (OBLIGACIONES DE {companySigla}).-
          </Text>{" "}
          Son, por otra parte, obligaciones de{" "}
          <Text style={styles.bold}>{companySigla}</Text>:{"\n\n"}- Proporcionar
          la información y los recursos necesarios para la correcta prestación
          de los servicios contratados.{"\n"}- Efectuar el pago acordado a{" "}
          <Text style={styles.bold}>LA EMPRESA</Text> una vez recibida y
          validada toda la documentación correspondiente.{"\n"}- Contratar, por
          su cuenta, un seguro Todo Riesgo para la carga.{"\n"}- Cumplir con
          todas las disposiciones establecidas en el presente contrato.
        </Text>

        {/* CLÁUSULA DECIMA QUINTA */}
        <Text style={styles.p}>
          <Text style={styles.bold}>DECIMA QUINTA.- (FUERZA MAYOR).-</Text> Ni{" "}
          <Text style={styles.bold}>LA EMPRESA</Text>, ni{" "}
          <Text style={styles.bold}>{companySigla}</Text>, deberán
          responsabilizarse el uno al otro, o considerarse en violación del
          Contrato, en razón a un atraso o incumplimiento en la ejecución de
          cualquiera de sus obligaciones en la medida y por el plazo en que el
          atraso o la imposibilidad de cumplimiento se deban a un evento de
          Fuerza Mayor.{"\n\n"}
          Para los efectos de este contrato, "Fuerza Mayor" se entenderá como
          cualquier acontecimiento, acto o evento fuera del control de la parte
          obligada, imprevisible o inevitable, incluso tras haber adoptado las
          precauciones necesarias.{"\n\n"}
          Constituirán Fuerza Mayor, entre otros que se adecuen a la precedente,
          los siguientes actos o eventos: Fenómenos de la naturaleza de carácter
          extraordinario, guerras (declaradas o no), actos de terrorismo,
          bloqueos, disturbios civiles, motines, insurrecciones, sabotajes,
          cuarentenas y epidemias. Actos de una autoridad gubernamental que no
          hayan sido inducidos voluntariamente o promovidos por la parte
          afectada, ni resulten de su incumplimiento contractual. imposibilidad
          de obtener, a tiempo y pese a los mejores esfuerzos, permisos o
          licencias necesarias según este contrato, excepto aquellos requeridos
          para el personal y subcontratistas del contratista.{"\n\n"}
          Cuando <Text style={styles.bold}>LA EMPRESA</Text> se viera afectada
          por un evento de fuerza mayor y no notificara a{" "}
          <Text style={styles.bold}>{companySigla}</Text> dentro del plazo de 24
          hrs impostergablemente, perderá el derecho de que dicho evento lo
          excuse o excluya de su responsabilidad para la correcta y oportuna
          prestación de los servicios, así como todo otro derecho que desde esta
          Clausula le asista.
        </Text>

        {/* CLÁUSULA DECIMA SEXTA */}
        <Text style={styles.p}>
          <Text style={styles.bold}>
            DECIMA SEXTA. - (EXIGIBILIDAD DEL CONTRATO).-
          </Text>{" "}
          Las obligaciones de las partes establecidas en este Contrato serán
          exigibles sin necesidad de constitución en mora ni citación previa
          alguna, misma a la que las partes renuncian expresamente.
        </Text>

        {/* CLÁUSULA DECIMA SEPTIMA */}
        <Text style={styles.p}>
          <Text style={styles.bold}>DECIMA SEPTIMA (CONFORMIDAD).-</Text> En
          señal de conformidad y aceptación con todas y cada una de las
          cláusulas insertas en el presente instrumento, las partes
          intervinientes suscriben en triple ejemplar con un mismo tenor.
        </Text>

        {/* LUGAR Y FECHA (Dinámico) */}
        <View style={{ alignItems: "center", marginTop: 20, marginBottom: 40 }}>
          <Text style={styles.bold}>
            {cuidadDeLaNotaria}, {formatearFecha(fechaContrato)}
          </Text>
        </View>

        {/* FIRMAS */}
        <View style={styles.signatures} wrap={false}>
          <View style={styles.signBox}>
            <View style={styles.signatureImageBox}>
              {legalRepresentativeSignatureSrc ? (
                <Image
                  src={legalRepresentativeSignatureSrc}
                  style={styles.signatureImage}
                />
              ) : null}
            </View>
            <View style={styles.signatureLine}>
              <Text style={tw("font-bold text-[10px]")}>
                {/* {company.legalRepresentativeName || "S/N"} */}
                Gabriela Miranda Garcia
              </Text>
              <Text style={tw("text-[9px]")}>
                C.I. {/* company.legalRepresentativeCI || "S/N"} */}
                4298322
              </Text>
              <Text style={tw("font-bold text-[9px] mt-1")}>
                {/* {companyName} */}
                RESPONSABLE DE IMPORTACIONES
              </Text>
            </View>
          </View>

          <View style={styles.signBox}>
            <View style={styles.signatureImageBox} />
            <View style={styles.signatureLine}>
              <Text style={tw("font-bold text-[10px]")}>
                {providerData.representante_legal || "Juan Carlos Rios Coca"}
              </Text>
              <Text style={tw("text-[9px]")}>
                C.I. {providerData.ci_dni || "4427058."}
              </Text>
              <Text style={tw("font-bold text-[9px] mt-1")}>
                REPRESENTANTE LEGAL {providerName || "TRANS CATALUNIA"}.
              </Text>
            </View>
          </View>
        </View>

        {/* <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => (`Página ${pageNumber} de ${totalPages}`)} fixed /> */}
      </Page>
    </Document>
  );

  return (
    <div className="min-w-[80vw]">
      <PDFViewer
        style={{
          width: "100%",
          height: window.innerHeight - 100,
        }}
        children={MyDoc}
      />
    </div>
  );
};
